// Helper functions for localStorage (moved outside component to avoid dependency issues)

const MAP_VIEW_STORAGE_PREFIX = 'fia-portal-nowcasting-map-view';
const SPLIT_MAP_VIEW_STORAGE_PREFIX = 'fia-portal-nowcasting-split-map-view';

/** Event key for per-event map view (season + circuit) */
export interface EventMapViewKey {
  season_year: number;
  circuit_id: string;
}

export interface SavedMapView {
  center: [number, number]; // [lng, lat]
  zoom: number;
}

export const getEventMapViewKey = (event: EventMapViewKey): string => {
  return `${MAP_VIEW_STORAGE_PREFIX}-${event.season_year}-${event.circuit_id}`;
};

export const loadMapViewForEvent = (event: EventMapViewKey): SavedMapView | null => {
  if (typeof window === 'undefined') return null;
  try {
    const key = getEventMapViewKey(event);
    const saved = localStorage.getItem(key);
    if (!saved) return null;
    const parsed = JSON.parse(saved) as { center?: [number, number]; zoom?: number };
    if (
      Array.isArray(parsed.center) &&
      parsed.center.length === 2 &&
      typeof parsed.zoom === 'number' &&
      parsed.zoom >= 0 &&
      parsed.zoom <= 22
    ) {
      return { center: parsed.center, zoom: parsed.zoom };
    }
  } catch (e) {
    console.warn('Failed to load map view from localStorage:', e);
  }
  return null;
};

export const resolveMapViewForEvent = (
  event: EventMapViewKey & { circuit: { latitude?: number | null; longitude?: number | null } },
  options?: {
    initialCenter?: [number, number];
    initialZoom?: number;
    disableViewPersistence?: boolean;
  },
): SavedMapView => {
  const circuitLat = event.circuit.latitude || 0;
  const circuitLon = event.circuit.longitude || 0;

  if (options?.initialCenter && options.initialZoom !== undefined) {
    return { center: options.initialCenter, zoom: options.initialZoom };
  }

  if (!options?.disableViewPersistence) {
    const savedView = loadMapViewForEvent(event);
    if (savedView) return savedView;
  }

  return {
    center: options?.initialCenter ?? [circuitLon, circuitLat],
    zoom: options?.initialZoom ?? 13,
  };
};

export const getEventKey = (event: EventMapViewKey & { start_date?: string }): string => {
  return `${event.season_year}-${event.circuit_id}-${event.start_date ?? ''}`;
};

export const saveMapViewForEvent = (
  event: EventMapViewKey,
  center: { lng: number; lat: number },
  zoom: number
): void => {
  if (typeof window === 'undefined') return;
  try {
    const key = getEventMapViewKey(event);
    const view: SavedMapView = { center: [center.lng, center.lat], zoom };
    localStorage.setItem(key, JSON.stringify(view));
  } catch (e) {
    console.warn('Failed to save map view to localStorage:', e);
  }
};

// ============================================================================
// Split Map View Storage (for the left map in split view)
// ============================================================================

export const getSplitMapViewKey = (event: EventMapViewKey): string => {
  return `${SPLIT_MAP_VIEW_STORAGE_PREFIX}-${event.season_year}-${event.circuit_id}`;
};

export const loadSplitMapViewForEvent = (event: EventMapViewKey): SavedMapView | null => {
  if (typeof window === 'undefined') return null;
  try {
    const key = getSplitMapViewKey(event);
    const saved = localStorage.getItem(key);
    if (!saved) return null;
    const parsed = JSON.parse(saved) as { center?: [number, number]; zoom?: number };
    if (
      Array.isArray(parsed.center) &&
      parsed.center.length === 2 &&
      typeof parsed.zoom === 'number' &&
      parsed.zoom >= 0 &&
      parsed.zoom <= 22
    ) {
      return { center: parsed.center, zoom: parsed.zoom };
    }
  } catch (e) {
    console.warn('Failed to load split map view from localStorage:', e);
  }
  return null;
};

export const resolveSplitMapViewForEvent = (
  event: EventMapViewKey & { circuit: { latitude?: number | null; longitude?: number | null } },
): SavedMapView => {
  const circuitLat = event.circuit.latitude || 0;
  const circuitLon = event.circuit.longitude || 0;
  const savedView = loadSplitMapViewForEvent(event);
  return savedView ?? { center: [circuitLon, circuitLat], zoom: 13 };
};

export const saveSplitMapViewForEvent = (
  event: EventMapViewKey,
  center: { lng: number; lat: number },
  zoom: number
): void => {
  if (typeof window === 'undefined') return;
  try {
    const key = getSplitMapViewKey(event);
    const view: SavedMapView = { center: [center.lng, center.lat], zoom };
    localStorage.setItem(key, JSON.stringify(view));
  } catch (e) {
    console.warn('Failed to save split map view to localStorage:', e);
  }
};

// Helper function to get storage key (exported for use in component)
export const getStorageKey = (source: 'radar' | 'tomorrow.io' | 'tomorrow.io-global' | 'radar-l2' | 'radar-l2-numeric', type: 'start' | 'end') => {
  const storageSource = source === 'radar-l2' || source === 'radar-l2-numeric' ? 'radar' : source === 'tomorrow.io-global' ? 'tomorrow.io' : source;
  return `nowcasting-live-${type}-minutes-${storageSource}`;
};

// Helper function to load from localStorage
export const loadLiveMinutes = (source: 'radar' | 'tomorrow.io' | 'tomorrow.io-global' | 'radar-l2' | 'radar-l2-numeric', type: 'start' | 'end', defaultValue: number): number => {
  try {
    const key = getStorageKey(source, type);
    const stored = localStorage.getItem(key);
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (type === 'start') {
        // Validate: must be between -60 and 0
        if (!isNaN(parsed) && parsed >= -60 && parsed <= 0) {
          return parsed;
        }
      } else {
        // Validate: must be between 1 and 60
        if (!isNaN(parsed) && parsed >= 1 && parsed <= 60) {
          return parsed;
        }
      }
    }
  } catch (error) {
    console.error(`Error restoring live${type === 'start' ? 'Start' : 'End'}Minutes from localStorage:`, error);
  }
  return defaultValue;
};
